import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { EditionViewStore } from "../../store";
import { EditionView } from "../view/EditionView";
import { EditionUpdateStore } from "../../store";
import { EditionUpdate } from "../update/EditionUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type EditionModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const EditionModalContent: React.FC<EditionModalContentProps> = observer(
  ({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new EditionUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new EditionViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <EditionView editionViewStore={storeInfo.store as EditionViewStore} />
        )}
        {storeInfo.type === "update" && (
          <EditionUpdate
            editionUpdateStore={storeInfo.store as EditionUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  }
);
